import * as d3 from 'd3';

import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import {Strand} from 'constants/MolbioConstants';
import {TRANSCRIPTION_COLOR, TRANSCRIPTION_CHIP_TEXT_COLOR} from 'molbio/maps/BiointelligentLinksConstants';
import CircularBiointelligentLinkAnnotationsView from 'molbio/maps/circular/BiointelligentLinkAnnotationsView';
import {SegmentsRingData} from 'molbio/maps/circular/CircularTypedefs';
import {transcriptionId} from 'molbio/maps/linear/TranscriptionHelpers';
import {RegionSelectable} from 'molbio/maps/selectables/Selectables';
import {TranscriptionMetadata} from 'molbio/TranscriptionsUtil';

interface TranscriptionAsAnnotationAttributes {
  transcriptionData: TranscriptionMetadata;
  sequenceStartIndex: number;
  sequenceLength: number;
}

interface TranscriptionAsAnnotation {
  get<K extends keyof TranscriptionAsAnnotationAttributes>(key: K): TranscriptionAsAnnotationAttributes[K];
  set: never;
}

class TranscriptionAsAnnotation extends RegionSelectable<Sequence> implements SegmentsRingData {
  uid(): string {
    return transcriptionId(this.get('transcriptionData'));
  }

  start(): number {
    return this.get('transcriptionData').start;
  }

  end(): number {
    return this.get('transcriptionData').end;
  }

  hover(): boolean {
    return false;
  }

  color(): string {
    return TRANSCRIPTION_COLOR;
  }

  strand(): Strand {
    return this.get('transcriptionData').isReverseComplement ? -1 : 1;
  }

  name(): string {
    const transcriptionData = this.get('transcriptionData');
    return transcriptionData.rnaSequence.name || 'Transcription';
  }

  displayLabel(): string {
    return this.name();
  }

  visible(): boolean {
    return true;
  }

  type(): string {
    return 'transcription';
  }

  click(sequence: Sequence, shiftKey: boolean = false): void {
    const selection = sequence.get('selection');
    if (shiftKey) {
      selection.expandRange(this.start(), this.end());
    } else {
      selection.setEndpoints(this.start(), this.end());
    }
  }

  dblClick(sequence: Sequence, shiftKey: boolean = false): void {
    this.click(sequence, shiftKey);
  }
}

/**
 * View for drawing transcriptions on the circular map as annotations.
 */
class CircularTranscriptionAnnotationsView extends CircularBiointelligentLinkAnnotationsView<TranscriptionAsAnnotation> {
  singularClassName = 'transcription-annotation';

  static initClass(): void {
    this.prototype.className = 'transcription-annotations';
    this.prototype.singularClassName = 'transcription-annotation';
    this.prototype.tagName = 'g';

    // @ts-expect-error
    this.prototype.contextMenuData = null;
  }

  override fill(_d: TranscriptionAsAnnotation): string {
    return TRANSCRIPTION_COLOR;
  }

  override fontColor(_d: TranscriptionAsAnnotation): string {
    return TRANSCRIPTION_CHIP_TEXT_COLOR;
  }

  override data(): Array<TranscriptionAsAnnotation> {
    if (!BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS) {
      return [];
    }
    const start = this.model.get('leftBP');
    const end = this.model.get('rightBP');
    const sequence = this.model.sequence();
    const transcriptionData = sequence.getTranscriptionsBetween(start, end);

    return transcriptionData.map(
      (transcriptionData) =>
        new TranscriptionAsAnnotation({
          transcriptionData,
          sequenceStartIndex: sequence.startIndex(),
          sequenceLength: sequence.length(),
        })
    );
  }

  clickAction(e: MouseEvent, d: TranscriptionAsAnnotation): void {
    const sequence = this.model.sequence();
    if (e.shiftKey) {
      d.click(sequence, true);
    } else {
      d.click(sequence, false);
    }
  }

  dblClickAction(e: MouseEvent, d: TranscriptionAsAnnotation): void {
    const sequence = this.model.sequence();
    d.dblClick(sequence, e.shiftKey);
  }

  override registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.model.sequence(), 'change:transcriptionMatches', () => {
      this.invalidateData();
    });
  }

  handleDrawContents(
    _selector: d3.Selection<SVGGElement, TranscriptionAsAnnotation, SVGGElement, unknown>
  ): void {
    // TODO(BNCH-136734): Draw inner details for transcription annotations
  }
}
CircularTranscriptionAnnotationsView.initClass();

export default CircularTranscriptionAnnotationsView;
